import { getCompanyName, getExternalLinks } from "../../../utils/country";
import { useTranslation } from "react-i18next";
import { OnlyIf } from "../../common/OnlyIf";
import AuthService from "../../auth/AuthService";
import { CountrySwitch } from "../../common/CountrySwitch";
import logo from "../../../assets/logo/logo.webp";
import "./footer.scss";

interface FooterLink {
  label: string;
  href?: string;
  dataCy: string;
}

/**
 * Mirrors the footer on bits-landing-v2: brand column, five link columns, a
 * divider bar, then the regulatory notices.
 *
 * The landing page hardcodes its labels and paths; here they come from the
 * translation bundle and `getExternalLinks()` so the copy stays localised and
 * the URLs stay country-aware.
 */
export const Footer = () => {
  const { t, ready } = useTranslation();
  if (!ready) {
    return null;
  }
  const externalLinks = getExternalLinks();

  const {
    twitterUrl,
    instagramUrl,
    termsConditionsUrl,
    termsUrl,
    policyPrivacyUrl,
    complaints,
    rentReporting,
    bitsStore,
    bitsStoreGiftCardList,
    helpCentre,
    missionAndValues,
    productComparison,
    vulnerableCustomers,
    termsConditionsUrlSimplified,
    termsUrlSimplified,
    policyPrivacyUrlSimplified,
    complaintsSimplified,
    marketplaceUrl,
    risksBenefitsUrl,
    freeCreditReportUrl,
    regulatoryStatusUrl,
    trustpilotUrl,
    usaSiteUrl,
    scoreInAppUrl,
    creditCardFaqsUrl,
    creditCardTermsAndConditionsUrl,
    creditCardFXCalculatorUrl,
    creditCard3dsFaqsUrl,
    creditCardPrivacyPolicyUrl,
    creditCardHomeUrl,
    blogPosts,
    diversityAndInclusion,
    aiChatUrl,
    assetValuationUrl,
    deleteAccountUrl,
    support,
  } = externalLinks;
  const currentYear = new Date().getFullYear();

  const columns: { title: string; titleDataCy: string; links: FooterLink[] }[] = [
    {
      title: t("navigation.externalNavigation.company"),
      titleDataCy: "company-title",
      links: [
        { label: t("navigation.externalNavigation.aiChat"), href: aiChatUrl, dataCy: "ai-chat" },
        { label: t("navigation.externalNavigation.scoreInApp"), href: scoreInAppUrl, dataCy: "score-in-app" },
        { label: t("navigation.externalNavigation.marketplace"), href: marketplaceUrl, dataCy: "marketplace" },
        { label: t("navigation.externalNavigation.rentReporting"), href: rentReporting, dataCy: "rent-reporting" },
        {
          label: t("navigation.externalNavigation.assetValuation"),
          href: assetValuationUrl,
          dataCy: "asset-valuation",
        },
        {
          label: t("navigation.externalNavigation.bitsStore.howItWorks"),
          href: bitsStore,
          dataCy: "bits-store",
        },
        {
          label: t("navigation.externalNavigation.bitsStore.giftCardList"),
          href: bitsStoreGiftCardList,
          dataCy: "bits-store-gift-card-list",
        },
        { label: t("navigation.externalNavigation.blogPosts"), href: blogPosts, dataCy: "bits-blog-posts" },
        {
          label: t("navigation.externalNavigation.risksBenefits"),
          href: risksBenefitsUrl,
          dataCy: "risks-benefits-link",
        },
        {
          label: t("navigation.externalNavigation.freeCreditReport"),
          href: freeCreditReportUrl,
          dataCy: "free-credit-report-link",
        },
        {
          label: t("navigation.externalNavigation.regulatoryStatus"),
          href: regulatoryStatusUrl,
          dataCy: "regulatory-status-link",
        },
        {
          label: t("navigation.externalNavigation.vulnerableCustomers"),
          href: vulnerableCustomers,
          dataCy: "vulnerable-customers-link",
        },
        {
          label: t("navigation.externalNavigation.productComparison"),
          href: productComparison,
          dataCy: "product-comparison-link",
        },
        {
          label: t("navigation.externalNavigation.missionAndValues"),
          href: missionAndValues,
          dataCy: "mission-and-values-link",
        },
        {
          label: t("navigation.externalNavigation.diversityAndInclusion"),
          href: diversityAndInclusion,
          dataCy: "diversity-and-inclusion-link",
        },
        { label: t("navigation.externalNavigation.helpCentre"), href: helpCentre, dataCy: "help-centre-link" },
        { label: t("navigation.externalNavigation.support"), href: support, dataCy: "support-link" },
      ],
    },
    {
      title: t("navigation.externalNavigation.creditCard"),
      titleDataCy: "credit-card",
      links: [
        {
          label: t("navigation.externalNavigation.creditCardHome"),
          href: creditCardHomeUrl,
          dataCy: "credit-card-home-link",
        },
        {
          label: t("navigation.externalNavigation.creditCardFaqs"),
          href: creditCardFaqsUrl,
          dataCy: "credit-card-faqs-link",
        },
        {
          label: t("navigation.externalNavigation.creditCardT&C"),
          href: creditCardTermsAndConditionsUrl,
          dataCy: "credit-card-t-c-link",
        },
        {
          label: t("navigation.externalNavigation.cardFXCalculator"),
          href: creditCardFXCalculatorUrl,
          dataCy: "credit-card-fx-calculator-link",
        },
        {
          label: t("navigation.externalNavigation.cardPrivacyPolicy"),
          href: creditCardPrivacyPolicyUrl,
          dataCy: "credit-card-privacy-policy-link",
        },
        {
          label: t("navigation.externalNavigation.card3DSFAQS"),
          href: creditCard3dsFaqsUrl,
          dataCy: "credit-card-3ds-faqs-link",
        },
      ],
    },
    {
      title: t("navigation.externalNavigation.legal"),
      titleDataCy: "legal-title",
      links: [
        { label: t("navigation.externalNavigation.termsOfUse"), href: termsConditionsUrl, dataCy: "terms-of-use-link" },
        { label: t("navigation.externalNavigation.storeBitsCard"), href: termsUrl, dataCy: "store-card-terms-link" },
        {
          label: t("navigation.externalNavigation.privacyPolicy"),
          href: policyPrivacyUrl,
          dataCy: "privacy-policy-link",
        },
        { label: t("navigation.externalNavigation.complaints"), href: complaints, dataCy: "complaints-link" },
        {
          label: t("navigation.externalNavigation.deleteAccount"),
          href: deleteAccountUrl,
          dataCy: "delete-account-link",
        },
      ],
    },
    {
      title: t("navigation.externalNavigation.legalSimplified"),
      titleDataCy: "legal-title-simplified",
      links: [
        {
          label: t("navigation.externalNavigation.termsOfUseSimplified"),
          href: termsConditionsUrlSimplified,
          dataCy: "terms-of-use-link-simplified",
        },
        {
          label: t("navigation.externalNavigation.storeBitsCardSimplified"),
          href: termsUrlSimplified,
          dataCy: "store-card-terms-link-simplified",
        },
        {
          label: t("navigation.externalNavigation.privacyPolicySimplified"),
          href: policyPrivacyUrlSimplified,
          dataCy: "privacy-policy-link-simplified",
        },
        {
          label: t("navigation.externalNavigation.complaintsSimplified"),
          href: complaintsSimplified,
          dataCy: "complaints-link-simplified",
        },
      ],
    },
    {
      title: t("navigation.externalNavigation.content"),
      titleDataCy: "content-title",
      links: [
        { label: t("navigation.externalNavigation.usaSite"), href: usaSiteUrl, dataCy: "usa-site-link" },
        { label: t("navigation.externalNavigation.twitter"), href: twitterUrl, dataCy: "twitter-link" },
        { label: t("navigation.externalNavigation.instagram"), href: instagramUrl, dataCy: "instagram-link" },
        { label: t("navigation.externalNavigation.trustpilot"), href: trustpilotUrl, dataCy: "trustpilot-link" },
      ],
    },
  ];

  return (
    // `bits-footer` is what the stylesheet actually targets: the US footer in
    // `components/common/footer.scss` styles a bare `.footer` too, and with
    // equal specificity the winner came down to CSS chunk order.
    <footer className="footer bits-footer" data-cy="footer">
      <div className="footer__inner">
        <div className="footer__grid">
          <div className="footer__brand">
            <img className="footer__logo" src={logo} alt="Bits" />

            <p className="footer__address">
              <span data-cy="company-name-title">{t("common.footer.uk.companyName")}</span>
              <br />
              <span data-cy="address">{t("common.footer.uk.address")}</span>
            </p>

            <div className="footer__contact">
              <a data-cy="email-support" href={`mailto:${t("common.footer.uk.emailSupport")}`}>
                {t("common.footer.uk.emailSupport")}
              </a>
              <a data-cy="contact-number-support" href={`tel:${t("common.footer.uk.contactSupport")}`}>
                {t("common.footer.uk.contactSupport")}
              </a>
              <span data-cy="contact-number-details">{t("common.footer.uk.contactNumberDetails")}</span>
            </div>

            <a
              className="footer__yc"
              href="https://www.ycombinator.com"
              target="_blank"
              rel="noopener noreferrer"
              data-cy="y-combinator-link"
            >
              <span aria-hidden="true" className="footer__ycMark">
                Y
              </span>
              <span>{t("common.footer.uk.backedBy")}</span>
            </a>
          </div>

          {columns.map((column) => (
            <div className="footer__col" key={column.titleDataCy}>
              <span className="footer__colTitle" data-cy={column.titleDataCy}>
                {column.title}
              </span>
              <ul className="footer__links">
                {column.links.map((link) => (
                  <li key={link.dataCy}>
                    <a href={link.href} target="_blank" rel="noopener noreferrer" data-cy={link.dataCy}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="footer__bar">
          <p className="footer__copy">
            <span data-cy="copy-right">
              {getCompanyName()} 2018 - {currentYear}
            </span>
            <span className="footer__dot" aria-hidden="true">
              ·
            </span>
            {t("common.footer.uk.regulatoryLine")}
          </p>

          <div className="footer__barRight">
            <span data-cy="languages-supported">{t("common.footer.uk.languageSupported")}</span>
            <OnlyIf condition={!AuthService.isAuthenticated()}>
              <div className="country-switch-wrapper">
                <CountrySwitch />
              </div>
            </OnlyIf>
          </div>
        </div>

        <div className="footer__legal">
          <p data-cy="uk-footer-legal-1">{t("common.footer.uk.text1")}</p>
          <p data-cy="uk-footer-legal-2">{t("common.footer.uk.text2")}</p>
          <p data-cy="uk-footer-legal-3">{t("common.footer.uk.text3")}</p>
          <p data-cy="uk-footer-trademark">{t("common.footer.uk.trademark")}</p>
        </div>
      </div>
    </footer>
  );
};
